import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, parseISO } from 'date-fns'
import { categoryColors, extendedCategoryColors } from '@/lib/constants'

/**
 * Merges Tailwind CSS classes with clsx and resolves conflicts
 * @param inputs - Class values to merge
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted currency string
 * @example formatCurrency(1234.56) // "$1,234.56"
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD'
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

/**
 * Formats a date using date-fns
 * @param date - Date string or Date object
 * @param formatStr - Format string (default: 'PPP')
 * @returns Formatted date string
 * @example formatDate('2024-01-15') // "Jan 15, 2024"
 */
export function formatDate(
  date: string | Date | null | undefined,
  formatStr: string = 'PPP'
): string {
  if (!date) return ''
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    if (isNaN(dateObj.getTime())) return 'Invalid Date'
    return format(dateObj, formatStr)
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid Date'
  }
}

/**
 * Formats a date as a relative time string
 * @param date - Date string or Date object
 * @returns Relative date string (e.g., "2 days ago", "Yesterday")
 */
export function formatRelativeDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  const now = new Date()
  const diffInDays = Math.floor(
    (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24)
  )

  if (diffInDays === 0) return 'Today'
  if (diffInDays === 1) return 'Yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`

  return `${Math.floor(diffInDays / 365)} years ago`
}


// Generate dynamic color scheme for categories using Nordic Sanctuary palette
export function generateCategoryColor(category: string): {
  bg: string
  text: string
  border: string
  hover: string
} {
  const normalizedCategory = category.toLowerCase()

  // 1. Check if it's a known static category first for perfect consistency
  if (normalizedCategory in categoryColors) {
    const staticColor = categoryColors[normalizedCategory as keyof typeof categoryColors]
    const matchingExtended = extendedCategoryColors.find(
      (c) => c.bg === staticColor.bg
    )
    return {
      ...staticColor,
      hover: matchingExtended?.hover ?? 'hover:bg-muted/70',
    }
  }

  // 2. Fall back to consistent hash for dynamic categories
  const hash = normalizedCategory
    .split('')
    .reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)

  // Ensure positive index
  const index = Math.abs(hash) % extendedCategoryColors.length

  // Return the color scheme without the name property
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { name, ...colorScheme } = extendedCategoryColors[index]
  return colorScheme
}

// File size formatter
export function formatFileSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB']

  if (bytes === 0) return '0 Bytes'

  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)

  return `${size.toFixed(1)} ${sizes[i]}`
}

// Generate file path for uploads
export function generateFilePath(
  userId: string,
  folder: string,
  fileName: string
): string {
  const timestamp = new Date().getTime()
  const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_')
  return `${userId}/${folder}/${timestamp}_${cleanFileName}`
}

// Check if warranty is expiring soon (within 30 days)
export function isWarrantyExpiringSoon(warrantyExpiry: string | null): boolean {
  if (!warrantyExpiry) return false

  const expiry = parseISO(warrantyExpiry)
  const now = new Date()
  const thirtyDaysFromNow = new Date()
  thirtyDaysFromNow.setDate(now.getDate() + 30)

  return expiry <= thirtyDaysFromNow && expiry >= now
}

// Check if warranty is expired
export function isWarrantyExpired(warrantyExpiry: string | null): boolean {
  if (!warrantyExpiry) return false

  const expiry = parseISO(warrantyExpiry)
  const now = new Date()

  return expiry < now
}

// Calculate days until due date
export function getDaysUntilDue(dueDate: string): number {
  const due = parseISO(dueDate)
  const now = new Date()
  const diffInTime = due.getTime() - now.getTime()
  return Math.ceil(diffInTime / (1000 * 3600 * 24))
}

// Check if task is overdue
export function isTaskOverdue(dueDate: string, status: string): boolean {
  if (status === 'completed') return false

  const due = parseISO(dueDate)
  const now = new Date()

  return due < now
}

// Generate initials from name
export function generateInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)
}

// Debounce function for search
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout

  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Generate random ID
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}
